import { ApiErrorResponse } from '../types/auth';
import { Sector, CreateSectorPayload, UpdateSectorPayload } from '../types/sector';
import { PaginationParams, PaginatedResponse, buildQueryString } from '../types/pagination';
import { SECTOR_ENDPOINTS } from '../utils/endPoints';
import { createApiClient } from '../utils/axiosConfig';
import axios from 'axios';

export type { ApiErrorResponse } from '../types/auth';

const apiClient = createApiClient();

export const getSectors = async (): Promise<Sector[]> => {
  try {
    const response = await apiClient.get<any>(SECTOR_ENDPOINTS.ALL);
    
    if (response.data.sectors && Array.isArray(response.data.sectors)) {
      return response.data.sectors;
    }
    
    if (Array.isArray(response.data)) {
      return response.data;
    }
    
    return [];
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to fetch sectors');
    }
    console.error('Get sectors error:', error);
    throw new Error('An unexpected error occurred while fetching sectors.');
  }
};

export interface SectorWithStores extends Sector {
  stores?: Array<{ id: number; store_name: string }>;
}

export const getSectorsWithStores = async (params?: PaginationParams): Promise<PaginatedResponse<SectorWithStores>> => {
  try {
    const qs = buildQueryString(params ?? {});
    const response = await apiClient.get<{ sectors: SectorWithStores[]; total: number; page: number; limit: number; totalPages: number }>(
      `${SECTOR_ENDPOINTS.ALL_WITH_STORES}${qs}`
    );
    const d = response.data;
    return {
      items: d.sectors ?? [],
      total: d.total ?? 0,
      page: d.page ?? 1,
      limit: d.limit ?? 20,
      totalPages: d.totalPages ?? 0,
    };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to fetch sectors with stores');
    }
    console.error('Get sectors with stores error:', error);
    throw new Error('An unexpected error occurred while fetching sectors.');
  }
};

export const getSectorsByStore = async (storeId: number): Promise<Sector[]> => {
  try {
    const response = await apiClient.get<any>(SECTOR_ENDPOINTS.BY_STORE(storeId));
    
    if (response.data.sectors && Array.isArray(response.data.sectors)) {
      return response.data.sectors;
    }
    
    return [];
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to fetch store sectors');
    }
    console.error('Get store sectors error:', error);
    throw new Error('An unexpected error occurred while fetching store sectors.');
  }
};

export const createSector = async (sectorData: CreateSectorPayload): Promise<{ sector: Sector, message: string }> => {
  try {
    const response = await apiClient.post<{ sector: Sector, message: string }>(SECTOR_ENDPOINTS.CREATE, sectorData);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to create sector');
    }
    console.error('Create sector error:', error);
    throw new Error('An unexpected error occurred while creating the sector.');
  }
};

export const updateSector = async (sectorId: number, sectorData: UpdateSectorPayload): Promise<{ sector: Sector, message: string }> => {
  try {
    const payload = {
      ...sectorData,
      sector_id: sectorId
    };
    
    const response = await apiClient.put<{ sector: Sector, message: string }>(SECTOR_ENDPOINTS.UPDATE, payload);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to update sector');
    }
    console.error('Update sector error:', error);
    throw new Error('An unexpected error occurred while updating the sector.');
  }
};

export const deleteSector = async (sectorId: number): Promise<{ message: string }> => {
  try {
    const response = await apiClient.delete<{ message: string }>(SECTOR_ENDPOINTS.DELETE(sectorId));
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to delete sector');
    }
    console.error('Delete sector error:', error);
    throw new Error('An unexpected error occurred while deleting the sector.');
  }
}; 